import React, {
  ChangeEvent, FC, memo,
  useMemo, useState, useEffect
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Box
} from "@mui/material";
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

// Styled row component using MUI with custom styling
const StyledTableRow = ({ children, onClick, ...props }: { children: React.ReactNode; onClick?: () => void }) => (
  <TableRow 
    sx={{
      transition: 'all 0.2s',
      '&:nth-of-type(odd)': {
        backgroundColor: '#ffffff',
      },
      '&:nth-of-type(even)': {
        backgroundColor: '#f9fafb',
      },
      '&:hover': {
        backgroundColor: '#f3f4f6',
        cursor: onClick ? 'pointer' : 'default'
      },
      '& td': {
        borderRight: '1px solid #e5e7eb',
        '&:last-child': {
          borderRight: 'none',
        }
      }
    }}
    onClick={onClick}
    {...props}
  >
    {children}
  </TableRow>
);

// Pagination Component
export const CustomPagination = ({ 
  count, 
  page, 
  onChange, 
  showFirstButton = true, 
  showLastButton = true 
}: {
  count: number;
  page: number;
  onChange: (event: ChangeEvent<unknown>, page: number) => void;
  showFirstButton?: boolean;
  showLastButton?: boolean;
}) => {
  const handlePrevious = () => {
    if (page > 1) {
      onChange({} as ChangeEvent<unknown>, page - 1);
    }
  };

  const handleNext = () => {
    if (page < count) {
      onChange({} as ChangeEvent<unknown>, page + 1);
    }
  };

  const handleFirst = () => {
    onChange({} as ChangeEvent<unknown>, 1);
  };

  const handleLast = () => {
    onChange({} as ChangeEvent<unknown>, count);
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    // Handle single page case
    if (count <= 1) {
      return count === 1 ? [1] : [];
    }

    for (let i = Math.max(2, page - delta); i <= Math.min(count - 1, page + delta); i++) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < count - 1) {
      rangeWithDots.push('...', count);
    } else if (count > 1) {
      rangeWithDots.push(count);
    }

    return rangeWithDots;
  };

  return (
    <div className="flex justify-center items-center gap-1 mt-2">
      {showFirstButton && (
        <button
          onClick={handleFirst}
          disabled={page === 1}
          className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          title="First page"
        >
          <ChevronsLeft size={16} />
        </button>
      )}
      
      <button
        onClick={handlePrevious}
        disabled={page === 1}
        className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
        title="Previous page"
      >
        <ChevronLeft size={16} />
      </button>

      {getVisiblePages().map((pageNum, index) => (
        <button
          key={index}
          onClick={() => typeof pageNum === 'number' ? onChange({} as ChangeEvent<unknown>, pageNum) : undefined}
          disabled={typeof pageNum !== 'number'}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-[2.5rem] ${
            pageNum === page
              ? 'bg-primary text-white shadow-md'
              : typeof pageNum === 'number'
              ? 'border border-gray-300 text-gray-700 hover:bg-gray-50 hover:-translate-y-px'
              : 'cursor-default text-gray-400'
          }`}
        >
          {pageNum}
        </button>
      ))}

      <button
        onClick={handleNext}
        disabled={page === count}
        className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
        title="Next page"
      >
        <ChevronRight size={16} />
      </button>

      {showLastButton && (
        <button
          onClick={handleLast}
          disabled={page === count}
          className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          title="Last page"
        >
          <ChevronsRight size={16} />
        </button>
      )}
    </div>
  );
};

// TypeScript interface
interface TableProps {
  data: any[];
  columns: ColumnDef<any>[];
  isFetching?: boolean;
  skeletonCount?: number;
  pageCount?: number;
  page?: (page: number) => void;
  search?: (search: string) => void;
  onClickRow?: (cell: Cell<any, unknown>, row: Row<any>) => void;
  searchLabel?: string;
  EmptyText?: string;
  children?: React.ReactNode | React.ReactElement;
  handleRow?: () => void;
  showSerialNumber?: boolean; // New prop to control S/N column visibility
  currentPage?: number; // Current page for S/N calculation
  pageSize?: number; // Page size for S/N calculation
}

// The main table component
const TableUI: FC<TableProps> = ({
  data,
  columns,
  isFetching,
  skeletonCount = 10,
  pageCount,
  onClickRow,
  page,
  EmptyText = "No result found!",
  handleRow,
  showSerialNumber = true, // Show S/N column by default
  currentPage = 1,
  pageSize = 10
}) => {
  const [paginationPage, setPaginationPage] = useState(1);

  // Sync internal pagination state with currentPage prop
  useEffect(() => {
    if (currentPage && currentPage !== paginationPage) {
      setPaginationPage(currentPage);
    }
  }, [currentPage, paginationPage]);

  const memoizedData = useMemo(() => data, [data]);
  
  // Create S/N column
  const serialNumberColumn: ColumnDef<any> = {
    id: 'serialNumber',
    header: 'S/N',
    cell: ({ row }) => {
      const actualCurrentPage = currentPage || paginationPage;
      const serialNumber = (actualCurrentPage - 1) * pageSize + row.index + 1;
      return (
        <div className="font-medium text-gray-800 dark:text-white/90">
          {serialNumber}
        </div>
      );
    },
    size: 60
  };

  // Combine columns with optional S/N column
  const memoizedColumns = useMemo(() => {
    if (showSerialNumber) {
      // Check if S/N column already exists
      const hasSerialNumber = columns.some(col => 
        (col as any).id === 'serialNumber' || 
        (col as any).accessorKey === 'serialNumber'
      );
      
      if (!hasSerialNumber) {
        return [serialNumberColumn, ...columns];
      }
    }
    return columns;
  }, [columns, showSerialNumber, currentPage, pageSize]);

  const { getHeaderGroups, getRowModel, getAllColumns } = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount,
  });

  const skeletons = Array.from({ length: skeletonCount }, (_, i) => i);
  const columnCount = getAllColumns().length;

  const handlePageChange = (
    _: ChangeEvent<unknown>,
    currentPage: number
  ) => {
    setPaginationPage(currentPage === 0 ? 1 : currentPage);
    page?.(currentPage === 0 ? 1 : currentPage);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper sx={{ 
        width: '100%', 
        borderRadius: 2, 
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)', 
        border: '1px solid #d1d5db',
        overflow: 'hidden'
      }}>
        {/* Make table height fit visible screen minus header/footer/padding. Adjust as needed. */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto', overflowX: 'auto' }}>
          <Table stickyHeader aria-label="sticky table" sx={{ borderCollapse: 'separate', minWidth: '100%' }}>
            <TableHead>
              {getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem',
                        backgroundColor: '#f9fafb',
                        borderBottom: '1px solid #9ca3af',
                        borderRight: '1px solid #d1d5db',
                        whiteSpace: 'nowrap',
                        padding: '14px 16px',
                        color: '#374151',
                        letterSpacing: '0.025em',
                        '&:last-child': {
                          borderRight: 'none',
                        },
                        '@media (max-width: 768px)': {
                          whiteSpace: 'normal',
                          fontSize: '0.78rem',
                          padding: '10px 12px'
                        }
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            
            <TableBody>
              {!isFetching && getRowModel()?.rows.length > 0 ? (
                getRowModel()?.rows.map((row) => (
                  <StyledTableRow key={row.id} onClick={handleRow}>
                    {row.getVisibleCells().map((cell) => {
                      const isActionsColumn = cell.column.id === 'actions';
                      return (
                        <TableCell
                          key={cell.id}
                          onClick={() => onClickRow?.(cell, row)}
                          sx={{
                            padding: '14px 16px',
                            fontSize: '0.875rem',
                            color: '#1f2937',
                            borderBottom: '1px solid #d1d5db',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '@media (max-width: 768px)': {
                              whiteSpace: 'normal',
                              padding: '12px',
                              fontSize: '0.85rem'
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                ))
              ) : isFetching ? (
                skeletons.map((skeleton) => (
                  <TableRow 
                    key={skeleton}
                    sx={{
                      '& td': {
                        borderRight: '1px solid #d1d5db',
                        borderBottom: '1px solid #d1d5db',
                        '&:last-child': {
                          borderRight: 'none',
                        }
                      }
                    }}
                  >
                    {Array.from({ length: columnCount }, (_, i) => i).map((elm) => (
                      <TableCell 
                        key={elm}
                        sx={{ padding: '14px 16px' }}
                      >
                        <Skeleton 
                          variant="rectangular" 
                          width="100%" 
                          height={24}
                          sx={{ borderRadius: 1 }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={columnCount}
                    sx={{
                      padding: '48px 32px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                      borderTop: '1px solid #d1d5db'
                    }}
                  >
                    <Box>
                      <Box sx={{ fontSize: '1.125rem', fontWeight: 500, color: '#4b5563', mb: 1 }}>
                        {EmptyText}
                      </Box>
                      <Box sx={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        No data to display at the moment
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {pageCount && page && pageCount > 1 ? (
          <Box sx={{ 
            paddingBottom: '10px',
            backgroundColor: '#f9fafb', 
            borderTop: '1px solid #d1d5db' 
          }}>
            <CustomPagination
              count={pageCount}
              page={paginationPage}
              onChange={handlePageChange}
              showFirstButton
              showLastButton
            />
          </Box>
        ):<></>}
      </Paper>
    </Box>
  );
};

export default memo(TableUI);